import { useRouter } from 'next/router';
import { useContext } from 'react';
import { useUrlModal } from '~/components/views/Onboarding/PlanPortfolio/modals/DepositSelectionModal/useUrlModal';
import { DepositTransferModalKey, UrlModalHelper, WalletGlobal } from '~/helpers/UrlModalHelper';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { accountRoutes } from '~/routes/account';
import { formatCurrencyRoundedCents } from '~/utils/currencyFormatter';
import { Button, Modal } from '~/vinovest-design-system/components';
import { IconAutopay, IconRightArrow, IconTransfer, IconWithdraw } from '~/vinovest-design-system/components/Icons';
import { PortfolioTypes } from '~/models/PortfolioType';
import { useTradingPortfolioQuery } from '~/hooks/queries/portfolios/trading/useTradingPortfolioQuery';
import { useManagedPortfolioQuery } from '~/hooks/queries/portfolios/managed/useManagedPortfolioQuery';

export interface WalletGlobalModalProps {
    cashBalance: number;
    tradingCashBalance: number;
}

export const WalletGlobalModal: React.FC<WalletGlobalModalProps> = ({ cashBalance, tradingCashBalance }) => {
    const { open, closeModal } = useUrlModal(WalletGlobal);
    const { data: tradingPortfolio } = useTradingPortfolioQuery();
    const { data: managedPortfolio } = useManagedPortfolioQuery();
    const { setDestinationPortfolio, setDefaultPaymentSourceID } = useContext(DepositContext);
    const router = useRouter();
    const defaultSource =
        tradingPortfolio?.cash?.amount && tradingPortfolio?.cash?.amount > 0
            ? tradingPortfolio?.id
            : managedPortfolio?.id;
    return (
        <Modal classnames={'max-w-[660px] w-11/12'} open={open} setOpen={closeModal} modalKey={WalletGlobal}>
            <div className={' p-[24px]'}>
                <div className={'flex items-stretch justify-center gap-4 flex-wrap'}>
                    <div className={'bg-gray-200 rounded-[20px] p-[20px] mx-auto w-full text-center text-[14px]'}>
                        <p className={'font-vinovestmedium text-gray-500 font-normal text-14 uppercase'}>
                            Total balance
                        </p>
                        <p className={'font-roslindale text-[44px] !font-light mt-[10px]'}>
                            {formatCurrencyRoundedCents(cashBalance + tradingCashBalance)}
                        </p>
                    </div>
                    <div
                        className={
                            'bg-gray-200 rounded-[20px] p-[20px] mx-auto max-w-[500px] w-full md:w-[48%] text-center text-[14px]'
                        }
                    >
                        <p className={'font-vinovestmedium text-gray-500 font-normal text-14 uppercase'}>
                            Managed Investing
                        </p>
                        <p className={'font-roslindale text-32 !font-light mt-[10px]'}>
                            {formatCurrencyRoundedCents(cashBalance)}
                        </p>

                        <Button
                            variant={'platinum'}
                            classnames={'w-full my-[15px] text-[16px]'}
                            onClick={() => {
                                closeModal();
                                setDestinationPortfolio(PortfolioTypes.managed);
                                UrlModalHelper.openModal(router, DepositTransferModalKey);
                            }}
                        >
                            Deposit
                        </Button>
                        <div className={'mt-2 text-12 text-gray-500'}>
                            Funds Vinovest will invest on your behalf to build and grow your portfolio.
                        </div>
                    </div>
                    <div
                        className={
                            'bg-gray-200 rounded-[20px] p-[20px] mx-auto w-full md:w-[48%] text-center text-[14px]'
                        }
                    >
                        <p className={'font-vinovestmedium text-gray-500 font-normal text-14 uppercase'}>
                            Available Balance
                        </p>
                        <p className={'font-roslindale text-32 !font-light mt-[10px]'}>
                            {formatCurrencyRoundedCents(tradingCashBalance)}
                        </p>

                        <Button
                            variant={'platinum'}
                            classnames={'w-full my-[15px] text-[16px]'}
                            onClick={() => {
                                closeModal();
                                setDestinationPortfolio(PortfolioTypes.trading);
                                UrlModalHelper.openModal(router, DepositTransferModalKey);
                            }}
                        >
                            Deposit
                        </Button>
                        <div className={'mt-2 text-12 text-gray-500'}>
                            Funds for self directed purchases or to cover storage and insurance fees.
                        </div>
                    </div>
                </div>
                <div className={'mt-[20px]'}>
                    <Button
                        variant={'custom'}
                        classnames={
                            'w-full h-[60px] items-center justify-between flex border-b-[1px] border-gray-200 border-t-[1px] border-solid'
                        }
                        onClick={(e) => {
                            e.preventDefault();
                            closeModal();
                            router.push(accountRoutes.autoPay.href);
                        }}
                    >
                        <span className={'flex gap-[12px] text-[14px]'}>
                            <IconAutopay stroke={`#242E35`} width={'21px'} height={'18px'} /> Setup auto-pay for fees
                        </span>
                        <IconRightArrow stroke={`#242E35`} width={'18px'} height={'12px'} />
                    </Button>
                </div>
                <div>
                    <Button
                        variant={'custom'}
                        classnames={'w-full h-[60px] items-center justify-between flex'}
                        onClick={() => {
                            closeModal();
                            setDefaultPaymentSourceID(defaultSource || '');
                            setDestinationPortfolio(PortfolioTypes.managed);
                            UrlModalHelper.openModal(router, DepositTransferModalKey);
                        }}
                    >
                        <span className={'flex gap-[12px] text-[14px]'}>
                            <IconTransfer stroke={`#242E35`} width={'19px'} height={'20px'} /> Transfer between accounts
                        </span>
                        <IconRightArrow stroke={`#242E35`} width={'18px'} height={'12px'} />
                    </Button>
                </div>
                <div>
                    <Button
                        variant={'custom'}
                        disabled={cashBalance <= 0}
                        classnames={
                            'w-full h-[60px] items-center justify-between flex border-b-[1px] border-gray-200 border-t-[1px] border-solid'
                        }
                        href={accountRoutes.withdrawalFunds.href}
                        onClick={(e) => {
                            e.preventDefault();
                            closeModal();
                            router.push(accountRoutes.withdrawalFunds.href);
                            // This is to ensure that the modal closes before navigating
                        }}
                    >
                        <span className={'flex gap-[12px] text-[14px]'}>
                            <IconWithdraw stroke={`#242E35`} width={'24px'} height={'24px'} /> Withdraw Funds
                        </span>
                        <IconRightArrow stroke={`#242E35`} width={'18px'} height={'12px'} />
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
